/**
 * 画面に出す言語の 1 か所（ビルドの env `VITE_KUMO_LANGUAGES` = カンマ区切り・founder 2026-10-05）。
 *
 * WHY: founder「日本語ページはしばらく必要ないです。日本語ページは完全に見えなくしてください」（2026-10-05）。
 *   既定（env 無し）は `en` だけ = 切り替えボタンを出さず、ブラウザの言語（navigator.languages）・保存済みの 'ja'
 *   （localStorage `kumo.console.locale`）があっても常に英語で出す。API へ送る Accept-Language（acceptLanguage.ts）も
 *   画面の locale から作るので 'en' になる = サーバー側（確認メールの言語・登録時の locale）も ja にならない。
 * 戻し方: ビルドで `VITE_KUMO_LANGUAGES=en,ja` = 今まで（2026-10-04 まで）と同じ（切り替えボタン・ブラウザの言語・保存した選択）。
 * WHY 日本語の辞書（i18n/ja*.ts）を消さないか: 「しばらく」= 後で戻す前提。辞書は typecheck で en と同じキーを持ち続ける。
 * WHY 保存済みの 'ja' を上書き・削除しないか: 戻したときに、その人が前に選んだ言語のまま開けるように（隠している間は読むだけで使わない）。
 * en は常に入れる: 文言の正（en.ts）で、どの辞書も欠けたキーは en に落ちる作り。`ja` だけを書いても en は残す（言語が 0 にならない）。
 *
 * このファイルは import を持たない（launchHidden.ts と同じ理由: `node --test` で型の除去だけで動かす。i18n/index.ts は React を読むので node で import できない）。
 * node の --test では import.meta.env が無い = 既定（en だけ）。
 */

export type Locale = 'en' | 'ja';

/**
 * 切り替えボタンの並び順（今までの LOCALES = ['ja', 'en'] と同じ）。
 * WHY env の書き順にしないか: `en,ja` と書いても `ja,en` と書いても今までの見た目と同じにするため
 */
const ORDER: readonly Locale[] = ['ja', 'en'];

/** env の値 → 出す言語（知らない値・空白・大文字は無視・正規化。en は常に入る）。空 / 未設定 = en だけ */
export function parseLanguages(raw: unknown): Locale[] {
  const want = new Set<string>(['en']);
  if (typeof raw === 'string') for (const s of raw.split(',')) want.add(s.trim().toLowerCase());
  return ORDER.filter((l) => want.has(l));
}

/**
 * 最初に出す言語。順番は今までの detectLocale と同じ: 保存済み → navigator.languages（最初に出てくる ja* / en*）→ en。
 * ただし出さない言語（enabled に無い）は保存済みでもブラウザの言語でも選ばない（2026-10-05 founder・日本語を隠す）。
 */
export function pickLocale(input: { saved: string | null | undefined; browser: readonly (string | null | undefined)[]; enabled: readonly Locale[] }): Locale {
  const ok = (l: string | null | undefined): l is Locale => (l === 'en' || l === 'ja') && input.enabled.includes(l);
  if (ok(input.saved)) return input.saved;
  // 最初に出てくる ja* / en* で決める（["en-US","ja"] の人は en、["ja","en"] の人は ja）
  for (const raw of input.browser) {
    const low = (raw ?? '').toLowerCase();
    const l: Locale | null = low.startsWith('ja') ? 'ja' : low.startsWith('en') ? 'en' : null;
    // 出さない言語が先に来たら次を見ずに en（["ja","en"] の人は ja を隠している間は en。今までの「最初の 1 つで決める」を崩さない）
    if (l) return ok(l) ? l : 'en';
  }
  return 'en';
}

/** 言語の切り替えボタンを出すか（出す言語が 2 つ以上のときだけ） */
export function showLanguageSwitch(enabled: readonly Locale[]): boolean {
  return enabled.length > 1;
}

/** このビルドで出す言語（VITE_KUMO_LANGUAGES・既定 = en だけ） */
export const ENABLED_LOCALES: readonly Locale[] = parseLanguages((import.meta as { env?: Record<string, unknown> }).env?.VITE_KUMO_LANGUAGES);
